import { useRef, type ReactNode } from 'react';
import type { Locale } from '@/strings/types';
import { useShellLocale } from './shellLocale';
import {
  IconCheckSquare,
  IconSearch,
  IconFunnel,
  IconChevronDown,
  IconChevronRight,
  IconMoreVertical,
  IconPencil,
  IconCheck,
  IconClose,
  IconActions,
  IconCalendar,
  IconTemplates,
} from './icons';
import TouchDot from './TouchDot';
import { useScrollFade } from './useScrollFade';
import {
  ROWS,
  FILTER_PROJECT,
  FILTERED_COUNT,
  TOTAL_ITEMS,
  OWNER_ROW_ID,
  OWNER_EDIT_VALUE,
  useActionsDemoState,
  type DemoRow,
  type Priority,
  type Aim,
  type Surface,
} from './useActionsDemo';
import './ActionsView.css';

/**
 * Tab 4 — the app's Action Items page (/actions).
 *
 * Ported from MoMP frontend/src/pages/actions/*, keeping the parts that carry
 * the tab's claim and dropping the ones a six-second look cannot bank:
 *
 *   KEPT   four KPI cards (which are filter BUTTONS in the app — the funnel in
 *          the corner is the affordance, not decoration), the meeting column
 *          with its project sub-line, single-click inline editing, sortable
 *          column headers, the filter popover with its result-count preview,
 *          and the ≤480 card list, which in the app is a different component
 *          rather than a reflowed table.
 *   DROPPED search (typing is a beat we do not have), the meeting popover, the
 *          row kebab menu's contents, delete, the undo toast, and every
 *          empty/error/skeleton state.
 *
 * Copy is inline for the same reason AppShell's is: product chrome in a mockup,
 * not site copy. Content policy and the data's provenance are documented in
 * useActionsDemo.
 *
 * ⚠️ AR COPY IS A PROPOSAL PENDING OWNER SIGN-OFF (style guide workflow, step 7).
 */

interface ActionsCopy {
  title: string;
  sub: string;
  search: string;
  filter: string;
  section: string;
  showing: string;
  of: string;
  filteredBy: string;
  clearAll: string;
  cancel: string;
  apply: string;
  /* A function, for the same reason LibraryView's sectionsN is one: the button
     previews 4 results filtered and 55 unfiltered, and Arabic counts those two
     differently — 3 to 10 take جمع القلة, 11 and up the singular accusative. A
     fixed «نتيجة» rendered «4 نتيجة», which is simply ungrammatical. */
  results: (n: number) => string;
  sheetTitle: string;
  any: string;
  anyTime: string;
  otherProject: string;
  /** Screen-reader names for the two unlabelled columns and the count group. */
  aDone: string;
  aActions: string;
  aCounts: string;
  cols: Record<'summary' | 'meeting' | 'owner' | 'priority' | 'deadline', string>;
  dims: Record<'status' | 'deadline' | 'priority' | 'project', string>;
  /** Keyed by the CSS tone key, never by the visible word — see PriorityText. */
  pri: Record<Priority, string>;
  kpis: Record<'next7' | 'pending' | 'overdue' | 'completed', { label: string; sub: string }>;
}

const COPY: Record<Locale, ActionsCopy> = {
  en: {
    title: 'Action Items',
    sub: 'Your meeting action items across all projects',
    search: 'Search tasks, owners, meetings...',
    filter: 'Filter',
    section: 'Action Items',
    showing: 'Showing',
    of: 'of',
    filteredBy: 'Filtered by',
    clearAll: 'Clear all',
    cancel: 'Cancel',
    apply: 'Apply',
    results: (n) => `${n} results`,
    sheetTitle: 'Filter action items',
    any: 'All',
    anyTime: 'Any time',
    otherProject: 'Coastal Walkway',
    aDone: 'Done',
    aActions: 'Actions',
    aCounts: 'Action item counts',
    cols: {
      summary: 'SUMMARY',
      meeting: 'MEETING',
      owner: 'OWNER',
      priority: 'PRIORITY',
      deadline: 'DEADLINE',
    },
    dims: {
      status: 'Status',
      deadline: 'Deadline',
      priority: 'Priority',
      project: 'Project',
    },
    pri: { High: 'High', Medium: 'Medium', Low: 'Low' },
    kpis: {
      next7: { label: 'NEXT 7 DAYS', sub: 'Deadlines this week' },
      pending: { label: 'PENDING', sub: 'Not yet completed' },
      overdue: { label: 'OVERDUE', sub: 'Past deadline' },
      completed: { label: 'COMPLETED', sub: 'Marked done' },
    },
  },
  ar: {
    title: 'المهام والإجراءات',
    /* «بنودك» rather than «البنود الخاصة بك» — §4 names that construction the
       commonest English-shaped padding in Arabic product copy, and كاف الخطاب
       carries the possession on its own. */
    sub: 'مهام وإجراءات اجتماعاتك في كل المشاريع',
    /* A placeholder in §14's "category" shape: what you may search by. */
    search: 'البحث في المهام والمسؤولين والاجتماعات...',
    filter: 'تصفية',
    section: 'المهام والإجراءات',
    /* مصدر, not «يعرض». A third-person verb in a counter reads as "he/it
       displays"; §14 wants the verbal noun for an interface label. */
    showing: 'عرض',
    of: 'من',
    /* «تصفية حسب», not «مصفاة حسب». Undiacritized, «مصفاة» is also the noun for
       a strainer or a refinery, and it is the first reading on a toolbar. */
    filteredBy: 'تصفية حسب',
    clearAll: 'مسح الكل',
    cancel: 'إلغاء',
    apply: 'تطبيق',
    results: (n) => (n <= 10 ? `${n} نتائج` : `${n} نتيجة`),
    /* مصدر + اسم, per §14's button-and-heading rule. */
    sheetTitle: 'تصفية المهام والإجراءات',
    any: 'الكل',
    anyTime: 'أي وقت',
    otherProject: 'ممشى الواجهة البحرية',
    aDone: 'الإنجاز',
    aActions: 'الإجراءات',
    aCounts: 'عدادات المهام والإجراءات',
    cols: {
      summary: 'الملخص',
      meeting: 'الاجتماع',
      owner: 'المسؤول',
      priority: 'الأولوية',
      deadline: 'الموعد النهائي',
    },
    dims: {
      status: 'الحالة',
      deadline: 'الموعد النهائي',
      priority: 'الأولوية',
      project: 'المشروع',
    },
    pri: { High: 'عالية', Medium: 'متوسطة', Low: 'منخفضة' },
    kpis: {
      /* «خلال 7 أيام», not «الأيام السبعة القادمة»: §14 wants the fewest words
         that carry the heading, and the card's own sub-line says the rest. */
      next7: { label: 'خلال 7 أيام', sub: 'مواعيد هذا الأسبوع' },
      pending: { label: 'قيد الإنجاز', sub: 'لم تكتمل بعد' },
      overdue: { label: 'متأخرة', sub: 'تجاوزت الموعد' },
      completed: { label: 'مكتملة', sub: 'وُضعت علامة الإنجاز' },
    },
  },
};

/** The page's strings in the enclosing shell's language. */
const useT = () => COPY[useShellLocale()];

/* The app's own mapping (actionsLib.priorityHue): high reads as an error tone,
   medium as a warning, low as info. Not a severity ramp we invented. */
const PRIORITY_TONE: Record<Priority, string> = {
  High: 'error',
  Medium: 'warning',
  Low: 'info',
};

const KPI_CARDS = [
  { key: 'next7', tone: 'scheduled' },
  { key: 'pending', tone: 'warning' },
  { key: 'overdue', tone: 'error' },
  { key: 'completed', tone: 'success' },
] as const;

/* The app's em-dash placeholder for an empty owner or deadline. It is UI, not
   copy, so the no-em-dash rule does not reach it. */
const EMPTY = '—';

function PriorityText({ value }: { value: Priority }) {
  /* `value` is the tone KEY and the label is looked up — it used to render the
     key itself, which is only correct while the key happens to be English. */
  const t = useT();
  return (
    <span className={`pn-status-text pn-status-text--${PRIORITY_TONE[value]} pn-status-text--subtle`}>
      <span className="pn-status-text__dot" />
      {t.pri[value]}
    </span>
  );
}

/** The hover cue on an editable cell — decoration, never a separate button. */
function Pencil() {
  return (
    <span className="pn-editcell__pencil" aria-hidden="true">
      <IconPencil size={13} />
    </span>
  );
}

/* ── KPI strip ───────────────────────────────────────────────────────────── */

function KpiStrip() {
  const demo = useActionsDemoState();
  const t = useT();
  const counts = demo?.counts;
  return (
    <div className="pnact__kpis" role="group" aria-label={t.aCounts}>
      {KPI_CARDS.map(({ key, tone }) => (
        /* A real <button>, as in the app: every card is a filter facet. The
           funnel cue is what says so, and dropping it would have turned four
           controls into four labels. */
        <button
          type="button"
          key={key}
          className={`pn-kpi-card pnact__kpi pnact__kpi--${tone}`}
          tabIndex={-1}
        >
          <span className="pnact__kpi-cue" aria-hidden="true">
            <IconFunnel size={13} />
          </span>
          <span className="pn-kpi-card__label">{t.kpis[key].label}</span>
          <span className="pn-kpi-card__value">{counts ? counts[key] : EMPTY}</span>
          <span className="pn-kpi-card__sub">{t.kpis[key].sub}</span>
        </button>
      ))}
    </div>
  );
}

/* ── Filter row + the two filter surfaces ────────────────────────────────── */

function FilterRow() {
  const demo = useActionsDemoState();
  const t = useT();
  const applied = demo?.filter === 'applied';
  return (
    <div className="pnact__filters">
      <span className="pnact__search">
        <span className="pnact__search-icon" aria-hidden="true">
          <IconSearch size={16} />
        </span>
        <span className="pnact__search-ph">{t.search}</span>
      </span>
      <button
        type="button"
        tabIndex={-1}
        ref={demo?.register('filter', 'wide')}
        className={`pnact__filterbtn${applied ? ' is-on' : ''}`}
      >
        <IconFunnel size={14} />
        {t.filter}
        {applied && <span className="pnact__filterbtn-count">1</span>}
        <IconChevronDown size={14} />
      </button>
    </div>
  );
}

/* Structure only. The unset VALUE each trigger shows is copy, so it is resolved
   per locale at render — a module-level constant would have frozen it to
   whichever language loaded first. */
const FACETS = [
  { id: 'status', Icon: IconCheck, unset: 'any' },
  { id: 'deadline', Icon: IconCalendar, unset: 'anyTime' },
  { id: 'priority', Icon: IconActions, unset: 'any' },
  { id: 'project', Icon: IconTemplates, unset: 'any' },
] as const;

/**
 * The facet list, shared by the popover and the bottom sheet — in the app these
 * are two components (ActionsFilterPanel / ActionsFilterSheet) around one set of
 * facets, and the difference between them is the chrome, not the contents.
 */
function FacetList({ surface }: { surface: Surface }) {
  const demo = useActionsDemoState();
  const locale = useShellLocale();
  const t = COPY[locale];
  const picked = demo?.filter === 'picked';
  return (
    <div className="pnact__facets">
      {FACETS.map(({ id, Icon, unset }) => (
        <div className="pnact__facet" key={id}>
          <span className="pnact__facet-name">
            <span className="pnact__facet-icon" aria-hidden="true">
              <Icon size={14} />
            </span>
            {t.dims[id]}
          </span>
          <span
            ref={id === 'project' ? demo?.register('project', surface) : undefined}
            className={`pnact__facet-trigger${id === 'project' && picked ? ' is-set' : ''}`}
          >
            {id === 'project' && picked ? FILTER_PROJECT[locale] : t[unset]}
            <IconChevronDown size={13} />
          </span>
        </div>
      ))}
      {/* The project options only unfold once the dim is opened, which is what
          the script's second press does. */}
      {picked && (
        <div className="pnact__facet-opts">
          <span className="pnact__facet-opt is-on">
            <span className="pnact__facet-mark">
              <IconCheck size={11} />
            </span>
            {FILTER_PROJECT[locale]}
          </span>
          <span className="pnact__facet-opt">
            <span className="pnact__facet-mark" />
            {t.otherProject}
          </span>
        </div>
      )}
    </div>
  );
}

function FilterFoot({ surface }: { surface: Surface }) {
  const demo = useActionsDemoState();
  const t = useT();
  const picked = demo?.filter === 'picked';
  return (
    <div className="pnact__ffoot">
      <span className="pnact__fclear">{t.clearAll}</span>
      <span className="pnact__ffoot-actions">
        <span className="pnact__fbtn pnact__fbtn--secondary">{t.cancel}</span>
        {/* The result count previews on the button BEFORE you commit — one of
            the page's nicer details, and free to show here. */}
        <span ref={demo?.register('apply', surface)} className="pnact__fbtn pnact__fbtn--primary">
          {`${t.apply} · ${t.results(picked ? FILTERED_COUNT : TOTAL_ITEMS)}`}
        </span>
      </span>
    </div>
  );
}

/* ── Rows ────────────────────────────────────────────────────────────────── */

function useVisibleRows(): readonly DemoRow[] {
  const demo = useActionsDemoState();
  const rows = ROWS[useShellLocale()];
  if (demo?.filter !== 'applied') return rows;
  /* The row's own flag, not a compare against the displayed project name — see
     DemoRow.inFilter for why that distinction is load-bearing. */
  return rows.filter((r) => r.inFilter);
}

/** Which of the two scripted checkbox aims, if either, this row carries. */
function checkAim(index: number): Aim | null {
  return index === 0 ? 'check-a' : index === 1 ? 'check-b' : null;
}

function OwnerCell({ row, surface }: { row: DemoRow; surface: Surface }) {
  const demo = useActionsDemoState();
  const committed = OWNER_EDIT_VALUE[useShellLocale()];
  const isTarget = row.id === OWNER_ROW_ID;
  const editing = isTarget && demo?.editingOwner;
  const value = isTarget && demo?.ownerCommitted ? committed : row.owner;

  if (editing) {
    /* Rendered already-focused, because it is mid-edit — the same shape as the
       app's AiEditText (.pn-ai-editfield__el, a teal-bordered input carrying the
       focus ring). The caret trails the value, so the field reads as live both
       before the value lands and after. */
    return (
      <span className="pnact__editfield">
        <span className="pnact__editfield-el">
          {demo?.ownerTyped && <span className="pnact__editfield-val">{committed}</span>}
          <span className="pnact__caret" />
        </span>
      </span>
    );
  }
  /* The pencil is a hover cue in the app, and nothing in a mockup hovers — so a
     static reader would never learn the cell is editable. Revealing it on the
     cell the dot is TRAVELLING to is what a real pointer would do anyway: the
     cue arrives with the finger, a beat before the field replaces it. */
  const aimed = isTarget && demo?.aim === 'owner';
  return (
    <span
      className={`pn-editable pnact__owner${aimed ? ' is-aimed' : ''}`}
      ref={isTarget ? demo?.register('owner', surface) : undefined}
    >
      {value || EMPTY}
      <Pencil />
    </span>
  );
}

/* ── Wide layout: the table ──────────────────────────────────────────────── */

function SortTh({ label, active }: { label: string; active?: boolean }) {
  return (
    <th className="pnact__th" scope="col">
      <span className={`pn-sort-th pn-sort-th--persist${active ? ' is-active' : ''}`}>
        {label}
        <span className="pn-sort-th__chev">
          <IconChevronDown size={12} />
        </span>
      </span>
    </th>
  );
}

function WideTable() {
  const demo = useActionsDemoState();
  const t = useT();
  const rows = useVisibleRows();
  return (
    <div className="pnact__tablewrap">
      <table className="pnact__table">
        <thead>
          <tr>
            <th className="pnact__th pnact__th--check" scope="col" aria-label={t.aDone} />
            <SortTh label={t.cols.summary} />
            <SortTh label={t.cols.meeting} />
            <SortTh label={t.cols.owner} />
            <SortTh label={t.cols.priority} />
            {/* Deadline is the app's default sort — ascending, so the most
                overdue item is the first thing you see. */}
            <SortTh label={t.cols.deadline} active />
            <th className="pnact__th pnact__th--menu" scope="col" aria-label={t.aActions} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const done = demo?.done.includes(row.id) ?? false;
            const aim = checkAim(i);
            return (
              <tr key={row.id} className={`pnact__row${done ? ' is-done' : ''}`}>
                <td className="pnact__cell pnact__cell--check">
                  <span
                    ref={aim ? demo?.register(aim, 'wide') : undefined}
                    className={`pnact__check${done ? ' is-checked' : ''}`}
                    role="checkbox"
                    aria-checked={done}
                    aria-label={row.summary}
                  >
                    {done && <IconCheck size={12} />}
                  </span>
                </td>

                <td className="pnact__cell pnact__cell--summary">
                  <div className="pnact__rowtitle">
                    <span className="pn-editable">
                      <bdi>{row.summary}</bdi>
                      <Pencil />
                    </span>
                  </div>
                  {row.details && (
                    <div className="pnact__sub">
                      <bdi>{row.details}</bdi>
                    </div>
                  )}
                </td>

                {/* The project rides as the meeting's sub-line — the app has no
                    project column, and this is the only place it appears in the
                    table. */}
                <td className="pnact__cell pnact__cell--meeting">
                  <span className="pnact__meetname">
                    <bdi>{row.meeting}</bdi>
                  </span>
                  {row.project && (
                    <div className="pnact__meetsub">
                      <bdi>{row.project}</bdi>
                    </div>
                  )}
                </td>

                <td className="pnact__cell pnact__cell--owner">
                  <OwnerCell row={row} surface="wide" />
                </td>

                <td className="pnact__cell pnact__cell--priority">
                  <span className="pn-editable">
                    <PriorityText value={row.priority} />
                    <Pencil />
                  </span>
                </td>

                <td
                  className={`pnact__cell pnact__cell--deadline${
                    !done && row.overdue ? ' is-overdue' : !done && row.soon ? ' is-soon' : ''
                  }`}
                >
                  <span className="pn-editable">
                    <bdi>{row.deadline || EMPTY}</bdi>
                    <Pencil />
                  </span>
                </td>

                <td className="pnact__cell pnact__cell--menu">
                  <span className="pnact__kebab" aria-hidden="true">
                    <IconMoreVertical size={18} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Compact layout: the card list ───────────────────────────────────────── */

/**
 * Below 480 the app swaps the table for a different component entirely
 * (ActionsTableMobile): a card list whose rows carry [checkbox · priority dot ·
 * title · deadline · chevron] and expand to a key/value block. Reflowing the
 * table instead would have been cheaper and would have diverged from the product
 * at exactly the width most visitors are on.
 *
 * One row is expanded at rest — the row the script edits. That gives the owner
 * cell a real target at this width AND shows the card's expanded shape, which is
 * otherwise a state a six-second visit would never reach.
 */
function CompactList() {
  const demo = useActionsDemoState();
  const t = useT();
  const rows = useVisibleRows();
  return (
    <ul className="pnact-m" role="list">
      {rows.map((row, i) => {
        const done = demo?.done.includes(row.id) ?? false;
        const aim = checkAim(i);
        const expanded = row.id === OWNER_ROW_ID;
        return (
          <li key={row.id} className={`pnact-m__row${done ? ' is-done' : ''}${expanded ? ' is-expanded' : ''}`}>
            <div className="pnact-m__head">
              <span
                ref={aim ? demo?.register(aim, 'compact') : undefined}
                className={`pnact__check${done ? ' is-checked' : ''}`}
                role="checkbox"
                aria-checked={done}
                aria-label={row.summary}
              >
                {done && <IconCheck size={12} />}
              </span>
              <span className={`pnact-m__pri pnact-m__pri--${PRIORITY_TONE[row.priority]}`} aria-hidden="true" />
              <span className="pnact-m__title">
                <bdi>{row.summary}</bdi>
              </span>
              <span className={`pnact-m__date${!done && row.overdue ? ' is-overdue' : ''}`}>
                <bdi>{row.deadline || EMPTY}</bdi>
              </span>
              <span className={`pnact-m__chev${expanded ? ' is-expanded' : ''}`} aria-hidden="true">
                <IconChevronRight size={10} />
              </span>
              {expanded && (
                <span className="pnact__kebab" aria-hidden="true">
                  <IconMoreVertical size={18} />
                </span>
              )}
            </div>

            {expanded && (
              <div className="pnact-m__detail">
                {row.details && (
                  <div className="pnact-m__detailsub">
                    <bdi>{row.details}</bdi>
                  </div>
                )}
                <dl className="pnact-m__kv">
                  <Kv label={t.cols.meeting}>
                    <bdi>{row.meeting}</bdi>
                    {row.project && (
                      <span className="pnact-m__kv-sub">
                        <bdi>{row.project}</bdi>
                      </span>
                    )}
                  </Kv>
                  <Kv label={t.cols.owner}>
                    <OwnerCell row={row} surface="compact" />
                  </Kv>
                  <Kv label={t.cols.priority}>
                    <PriorityText value={row.priority} />
                  </Kv>
                  <Kv label={t.cols.deadline}>
                    <bdi>{row.deadline || EMPTY}</bdi>
                  </Kv>
                </dl>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Kv({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pnact-m__kvrow">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/* ── The page ────────────────────────────────────────────────────────────── */

export default function ActionsView() {
  const demo = useActionsDemoState();
  const locale = useShellLocale();
  const t = COPY[locale];
  const applied = demo?.filter === 'applied';
  const panelOpen = demo?.filter === 'open' || demo?.filter === 'picked';
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade(scrollRef);

  return (
    <div className="pnact">
      <div className="pnact__scroll pn-scroll" ref={scrollRef}>
        <header className="pnact__head">
          <div className="pnact__titlerow">
            <span className="pnact__headicon" aria-hidden="true">
              <IconCheckSquare size={22} />
            </span>
            <h1 className="pnact__title">{t.title}</h1>
          </div>
          <p className="pnact__headsub">{t.sub}</p>
        </header>

        <KpiStrip />
        <FilterRow />

        {applied && (
          <div className="pnact__chips">
            <span className="pnact__chips-lead">{t.filteredBy}</span>
            <span className="pnact__chip">
              <span className="pnact__chip-key">{t.dims.project}</span>
              <span className="pnact__chip-val">{FILTER_PROJECT[locale]}</span>
              <span className="pnact__chip-x" aria-hidden="true">
                <IconClose size={12} />
              </span>
            </span>
            <span className="pnact__chips-clear">{t.clearAll}</span>
          </div>
        )}

        <h2 className="pnact__section">
          {applied
            ? `${t.showing} ${FILTERED_COUNT} ${t.of} ${TOTAL_ITEMS}`
            : `${t.section} · ${TOTAL_ITEMS}`}
        </h2>

        {/* Both layouts render and a container query hides one — the same shape
            the recorder uses for its three transports. It is what lets the touch
            dot resolve its target by what is actually laid out, rather than
            keeping a second copy of the 480px threshold in JS. */}
        <WideTable />
        <CompactList />
      </div>

      {/* Popover ≥480, bottom sheet below — the app's own split. */}
      {panelOpen && (
        <>
          <div className="pnact__fpop">
            <FacetList surface="wide" />
            <FilterFoot surface="wide" />
          </div>
          <div className="pnact__fsheet-scrim">
            <div className="pnact__fsheet">
              <span className="pnact__fsheet-grab" aria-hidden="true" />
              <div className="pnact__fsheet-head">
                <h3 className="pnact__fsheet-title">{t.sheetTitle}</h3>
                <span className="pnact__fsheet-x" aria-hidden="true">
                  <IconClose size={16} />
                </span>
              </div>
              <FacetList surface="compact" />
              <FilterFoot surface="compact" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The scripted finger. Lives at shell level so it can reach the sidebar too. */
export function ActionsTouchDot() {
  const demo = useActionsDemoState();
  if (!demo) return null;
  return (
    <TouchDot
      aim={demo.aimKey}
      resolve={demo.resolve}
      /* Queried rather than held as a ref: this component is mounted in the
         shell's overlay slot, a different subtree from the page it scrolls, and
         one page renders at a time inside a frame. */
      scroller={() => document.querySelector<HTMLElement>('.pnact__scroll')}
      visible={demo.dotVisible}
      pressing={demo.pressing}
    />
  );
}
